/* CHEER LANE DELEGATION, the client half. PURE, ZERO IMPORTS.
 *
 * Brian, 2026-10-29: "I want to create a toggle specific for her so she can
 * only control her 3." The cheer lane lead oversees cheer and allocates to the
 * other two lane members. She does NOT hold "Manage Assignments", which would
 * let her reassign any record in the company. She holds a narrower
 * grant, `cc_lane_delegate`, that the backend enforces in the assignment lock.
 *
 * ⚠️ THIS FILE DECIDES WHAT GETS DRAWN, NOTHING MORE (CLAUDE.md hard rule 5).
 * sr-portal-backend re-decides every clause on POST /api/champion-update and
 * refuses with `lane_record_outside`, `lane_destination_outside`,
 * `lane_record_not_cheer` or `lane_cannot_clear`. The rules below mirror that
 * contract so a control is never drawn that the server will refuse, and so the
 * bulk bar can say which ticked rows it is skipping BEFORE it sends anything.
 *
 * ⚠️ THE LANE COMES FROM THE SERVER. `lane.members` and `lane.sportPattern`
 * arrive on GET /api/permissions/me. No rep name is typed in this file; a
 * hand-kept copy of the lane would drift from cheer-territory.js the first time
 * somebody joined the desk.
 */

const S = (v) => String(v == null ? '' : v).trim()
const lower = (v) => S(v).toLowerCase()

/** The grant key the backend stores. Underscored, like cc_rep_assign. */
export const LANE_DELEGATE = 'cc_lane_delegate'
export const LANE_DELEGATE_LABEL = 'Cheer Lane Delegation'

/** Mirrors the client's own "nobody" spellings in ownerName(). */
const NOBODY = new Set(['', 'unassigned', 'unknown', 'none', 'n/a', 'null', 'undefined'])

/** Normalise the server's lane object. Returns null when it is unusable. */
export function readLane(lane) {
  if (!lane || typeof lane !== 'object') return null
  const members = Array.isArray(lane.members) ? lane.members.map(S).filter(Boolean) : []
  if (!members.length) return null
  return {
    key: S(lane.key) || 'cheer',
    label: S(lane.label) || 'Cheer',
    lead: S(lane.lead) || null,
    members,
    sportPattern: S(lane.sportPattern) || 'cheer|spirit',
  }
}

/** Is this display name one of the lane's members? Exact, case-insensitive. */
export function inLane(lane, name) {
  const n = lower(name)
  if (NOBODY.has(n)) return false
  return !!lane && lane.members.some((m) => lower(m) === n)
}

/* ⚠️ THE RAW PROPERTY, BECAUSE THAT IS WHAT THE SERVER READS. The normalized
 * `rep` falls back to `sr_lead_rep`; the server's lane check reads
 * `sr_assigned_rep` and nothing else. Per-RECORD fallback, as in fieldEdit.js:
 * a record carrying `raw` is judged on `raw`, and a missing key means EMPTY. */
function ownerOf(record) {
  if (record?.raw && typeof record.raw === 'object') return S(record.raw.sr_assigned_rep)
  const v = record?.sr_assigned_rep ?? record?.rep ?? ''
  return typeof v === 'object' ? S(v?.name) : S(v)
}

function sportOf(record) {
  if (record?.raw && typeof record.raw === 'object') return S(record.raw.sr_sport)
  return S(record?.sr_sport ?? record?.sport)
}

/** Does the record's sport fall inside the lane? Blank sport is NOT inside. */
export function isLaneSport(lane, record) {
  if (!lane) return false
  const sport = sportOf(record)
  if (!sport) return false
  let re
  try { re = new RegExp(lane.sportPattern, 'i') } catch { return false }
  return re.test(sport)
}

/**
 * May a lane delegate reassign THIS record? Mirrors the server's checks in the
 * order it makes them, so the reason shown is the one the server would give.
 *
 * @returns {{ ok: boolean, code?: string, why?: string }}
 */
export function laneEligibility(lane, record) {
  if (!lane) return { ok: false, code: 'no_lane', why: 'The lane could not be read, so nothing is drawn.' }
  const owner = ownerOf(record)
  if (!inLane(lane, owner)) {
    return {
      ok: false, code: 'lane_record_outside',
      why: owner && !NOBODY.has(lower(owner))
        ? `Owned by ${owner}, who is outside the ${lane.label} lane. Only a manager can move it.`
        : `Unassigned. ${LANE_DELEGATE_LABEL} moves records already in the lane; a manager places new ones.`,
    }
  }
  if (!isLaneSport(lane, record)) {
    return {
      ok: false, code: 'lane_record_not_cheer',
      why: `Not a ${lane.label.toLowerCase()} record${sportOf(record) ? ` (${sportOf(record)})` : ' (no sport on the record)'}. ${LANE_DELEGATE_LABEL} covers ${lane.label.toLowerCase()} only.`,
    }
  }
  return { ok: true }
}

/** The only destinations a lane delegate may choose. Never blank. */
export function laneOptions(lane) {
  return lane ? lane.members.slice() : []
}

/** Would the server accept this destination from a lane delegate? */
export function laneDestinationOk(lane, to) {
  return inLane(lane, to)
}

/**
 * How the picker should render for this viewer on this record.
 *
 *   full      Manage Assignments: the whole team, unchanged behaviour
 *   lane      the delegate's picker: the lane members only
 *   readonly  nothing to pick; `why` says why
 *
 * @param {{allowed:boolean, why?:string}} manage  the resolved Manage Assignments gate
 * @param {{allowed:boolean, lane:object|null}} delegate  the resolved lane grant
 */
export function pickerMode(manage, delegate, record) {
  if (manage?.allowed) return { mode: 'full', why: '' }
  if (delegate?.allowed && delegate.lane) {
    const e = laneEligibility(delegate.lane, record)
    if (e.ok) return { mode: 'lane', why: '', options: laneOptions(delegate.lane) }
    return { mode: 'readonly', why: e.why, code: e.code }
  }
  return { mode: 'readonly', why: manage?.why || '' }
}

/**
 * Split a bulk selection for a lane delegate. Rows outside the lane are
 * SKIPPED and named, never sent: a batch that half-lands on refusals is a
 * result nobody can read.
 */
export function partitionForLane(lane, rows, to) {
  const send = [], skip = []
  if (!laneDestinationOk(lane, to)) {
    for (const r of rows || []) skip.push({ record: r, code: 'lane_destination_outside',
      why: `${S(to) || 'A blank destination'} is not in the ${lane?.label || 'lane'}.` })
    return { send, skip }
  }
  for (const r of rows || []) {
    const e = laneEligibility(lane, r)
    if (e.ok) send.push(r)
    else skip.push({ record: r, code: e.code, why: e.why })
  }
  return { send, skip }
}

/**
 * Resolve the lane grant from a /api/permissions/me payload.
 * Keyed on the boolean alias first; the permissions map key second.
 */
export function resolveLaneDelegation(payload) {
  const lane = readLane(payload?.lane)
  const granted = payload?.canDelegateLane === true
    || payload?.permissions?.[LANE_DELEGATE] === true
  return { allowed: granted && !!lane, granted, lane }
}
